// components/CommandPalette.tsx — Cmd/Ctrl+K launcher for navigation + quick actions.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import type { Theme } from '../lib/theme';
import {
  ChatIcon,
  DocsIcon,
  GitIcon,
  KeyIcon,
  LogoutIcon,
  MonitorIcon,
  MoonIcon,
  NotesIcon,
  SearchIcon,
  SettingsIcon,
  SunIcon,
  VoiceIcon,
} from './icons';

/** Open the command palette from anywhere (e.g. a top-bar button). */
export function openCommandPalette() {
  window.dispatchEvent(new Event('command-palette:open'));
}

interface Command {
  id: string;
  label: string;
  hint?: string;
  icon: React.ReactNode;
  run: () => void;
  keywords?: string;
}

export function CommandPalette() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setActive(0);
  }, []);

  const commands = useMemo<Command[]>(() => {
    const go = (to: string) => () => {
      navigate(to);
      close();
    };
    const theme = (t: Theme) => () => {
      setTheme(t);
      close();
    };
    return [
      {
        id: 'chat',
        label: 'Go to Chat',
        icon: <ChatIcon />,
        run: go('/app'),
        keywords: 'home talk',
      },
      {
        id: 'docs',
        label: 'Upload a document',
        icon: <DocsIcon />,
        run: go('/app/upload'),
        keywords: 'file pdf',
      },
      {
        id: 'notes',
        label: 'Capture a note',
        icon: <NotesIcon />,
        run: go('/app/notes'),
        keywords: 'write',
      },
      {
        id: 'git',
        label: 'Connect a repository',
        icon: <GitIcon />,
        run: go('/app/git'),
        keywords: 'github repo commits',
      },
      {
        id: 'voice',
        label: 'Think aloud (Voice)',
        icon: <VoiceIcon />,
        run: go('/app/voice'),
        keywords: 'audio speak',
      },
      {
        id: 'agents',
        label: 'Manage agent tokens',
        icon: <KeyIcon />,
        run: go('/app/agents'),
        keywords: 'mcp api token scope',
      },
      {
        id: 'settings',
        label: 'Open Settings',
        icon: <SettingsIcon />,
        run: go('/app/settings'),
        keywords: 'preferences keys',
      },
      { id: 'theme-system', label: 'Theme: System', icon: <MonitorIcon />, run: theme('system') },
      { id: 'theme-light', label: 'Theme: Light', icon: <SunIcon />, run: theme('light') },
      { id: 'theme-dark', label: 'Theme: Dark', icon: <MoonIcon />, run: theme('dark') },
      {
        id: 'signout',
        label: 'Sign out',
        icon: <LogoutIcon />,
        run: () => {
          close();
          void signOut();
        },
      },
    ];
  }, [navigate, setTheme, signOut, close]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter((c) => (c.label + ' ' + (c.keywords ?? '')).toLowerCase().includes(q));
  }, [commands, query]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    }
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('command-palette:open', onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('command-palette:open', onOpen);
    };
  }, []);

  useEffect(() => {
    if (open) {
      setActive(0);
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  if (!open) return null;

  const onInputKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      results[active]?.run();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-[12vh] backdrop-blur-sm animate-fade-in"
      role="button"
      tabIndex={-1}
      aria-label="Close command palette"
      onClick={close}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        className="w-full max-w-xl overflow-hidden rounded-2xl border border-line/10 bg-surface shadow-elevated animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-line/10 px-4">
          <SearchIcon className="h-5 w-5 text-fg-subtle" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKey}
            placeholder="Search actions and pages…"
            aria-label="Command"
            className="w-full bg-transparent py-3.5 text-sm text-fg outline-none placeholder:text-fg-subtle"
          />
          <span className="kbd">Esc</span>
        </div>
        <ul className="max-h-80 overflow-y-auto p-2" role="listbox" aria-label="Commands">
          {results.length === 0 ? (
            <li className="px-3 py-6 text-center text-sm text-fg-subtle">No matching commands</li>
          ) : (
            results.map((c, i) => (
              <li key={c.id} role="option" aria-selected={i === active}>
                <button
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onClick={c.run}
                  className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition-colors ${
                    i === active ? 'bg-brand/10 text-brand' : 'text-fg hover:bg-surface-3'
                  }`}
                >
                  <span className={i === active ? 'text-brand' : 'text-fg-muted'}>{c.icon}</span>
                  {c.label}
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}
